<template>
  <header class="witcher-header">
    <div class="header-container">
      <!-- Main Header Row -->
      <div class="main-header">
        <!-- Column 1: Logo -->
        <div class="logo-section">
          <div class="logo-box">
            <theme-logo alt-text="Logo" class="logo-image" />
          </div>
        </div>

        <!-- Column 2: Action Section (2 rows) -->
        <div class="action-section">
          <!-- Row 1: Action buttons + Menu -->
          <div class="action-row">
            <!-- Logged Out State -->
            <transition name="auth-fade" mode="out-in">
              <div v-if="!isUserLoggedIn" key="logged-out" class="auth-buttons">
                <b-button
                  class="action-btn theme-outline-btn"
                  @click="showLoginModal"
                >
                  เข้าสู่ระบบ
                </b-button>
                <b-button
                  class="action-btn theme-filled-btn"
                  @click="showRegisterModal"
                >
                  สมัครสมาชิก
                </b-button>
              </div>
              <!-- Logged In State - Minimal Design -->
              <div v-else key="logged-in" class="user-controls">
                <div class="balance-box">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="14"
                    class="balance-icon"
                  />
                  <span class="balance-value">{{
                    formatNumber(animatedBalance)
                  }}</span>
                </div>
                <div class="diamond-box">
                  <span class="diamond-icon">💎</span>
                  <span class="diamond-value">{{
                    formatNumber(animatedDiamondBalance)
                  }}</span>
                </div>
                <button class="menu-btn" @click="toggleSidebar">
                  <span class="menu-line" />
                  <span class="menu-line short" />
                  <span class="menu-line" />
                </button>
              </div>
            </transition>
          </div>

          <!-- Row 2: Announcement section -->
          <!-- <div class="announcement-section">
            <feather-icon
              icon="Volume2Icon"
              size="16"
              class="announcement-icon"
            />
            <div class="announcement-bar">
              <div class="marquee-container">
                <div class="marquee-text">
                  {{ announcementText }}
                </div>
              </div>
            </div>
          </div> -->
        </div>
      </div>

      <!-- Quick Action Buttons Row -->
      <!-- <div class="quick-actions-row">
        <div class="action-block">
          <div class="action-icon">🎰</div>
          <div class="action-label">ชวนเพื่อน</div>
        </div>
        <div class="action-block">
          <div class="action-icon">⚽</div>
          <div class="action-label">ภารกิจ</div>
        </div>
        <div class="action-block">
          <div class="action-icon">💰</div>
          <div class="action-label">ฝากประจำ</div>
        </div>
      </div> -->
    </div>

    <!-- Register Modal -->
    <register-modal
      v-if="showModal"
      :show="showModal"
      :promo-id="promoIdFromEvent"
      @close="closeRegisterModal"
      @register="handleRegister"
    />

    <!-- Login Modal -->
    <login-modal
      :show="showLoginModalVisible"
      @close="closeLoginModal"
      @login="handleLogin"
    />
  </header>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import { isUserLoggedIn } from '@/auth/utils'

// Lazy load auth modals to reduce initial bundle size (~100KB savings)
const RegisterModal = () =>
  import(
    /* webpackChunkName: "auth-modals" */ '../../../../views/v3/component/RegisterModal.vue'
  )
const LoginModal = () =>
  import(
    /* webpackChunkName: "auth-modals" */ '../../../../views/v3/component/LoginModal.vue'
  )

export default {
  name: 'WitcherHeader',
  components: {
    BButton,
    RegisterModal,
    LoginModal
  },
  props: {
    announcementText: {
      type: String,
      default:
        '🌟 เว็บรวมเกมส์อันดับ 1 ของไทย เหนือกว่า ในทุกด้าน มีทุกเกมส์ มีทุกค่าย ให้เลือกเล่นมากมาย ระบบฝาก-ถอน ออโต้เพียง 1 วิ'
    }
  },
  data() {
    return {
      showModal: false,
      showLoginModalVisible: false,
      userBalance: 0,
      userLoggedIn: isUserLoggedIn(), // Initialize immediately to prevent flash
      animatedBalance: 0,
      animatedDiamondBalance: 0,
      animationDuration: 800,
      promoIdFromEvent: null // Store promoId from event to pass to RegisterModal
    }
  },
  methods: {
    toggleSidebar() {
      this.$emit('menu-click')
    },
    showRegisterModal(eventData) {
      // Accept promoId from event payload (passed from HomePage.vue)
      this.promoIdFromEvent = eventData?.promoId || null
      this.showModal = true
    },
    closeRegisterModal() {
      this.showModal = false
      this.promoIdFromEvent = null // Clear promoId when modal closes
    },
    handleRegister(formData) {
      console.log('Registration data:', formData)
      this.$emit('registration', formData)
    },
    showLoginModal() {
      this.showLoginModalVisible = true
    },
    closeLoginModal() {
      this.showLoginModalVisible = false
    },
    handleLogin(formData) {
      console.log('Login data:', formData)
      this.$emit('login', formData)
      this.$emit('login-success', formData)

      // Emit login success event for reactive updates
      this.$eventBus.$emit('user-logged-in')
    },
    formatNumber(num) {
      if (!num) return '0'
      return new Intl.NumberFormat('th-TH').format(num)
    },
    loadInitialBalance() {
      // TODO: Load initial balance from API or store
      // For now, set a default value
      this.userBalance = 0
    },
    handleBalanceUpdate(newBalance) {
      this.userBalance = newBalance
      this.animateBalanceChange(newBalance)
    },
    handleUserLogin() {
      console.log('[WitcherHeader] User logged in - updating state')
      this.userLoggedIn = true
      this.getCredit()
    },
    handleUserLogout() {
      console.log('[WitcherHeader] User logged out - clearing state')
      this.userLoggedIn = false
      this.userBalance = 0
      this.animatedBalance = 0
      this.animatedDiamondBalance = 0
    },
    async getCredit() {
      try {
        // Check free promo status first
        await this.checkFreePromo()

        // Refresh the active-promo game restriction snapshot (non-blocking)
        this.fetchGameRestriction({ force: true })

        // Fetch credit (will use mock or real based on promo status)
        await this.fetchCredit()

        // Use displayBalance getter which handles mock/real credit
        const balance = this.$store.getters['member/displayBalance']
        const diamondBalance = this.$store.getters['member/diamondBalance']
        this.userBalance = balance
        this.animateBalanceChange(this.userBalance)
        this.animateDiamondBalanceChange(diamondBalance)
        console.log(
          '[WitcherHeader] Balance loaded from store:',
          this.userBalance,
          'diamond:',
          diamondBalance,
          'isMock:',
          this.$store.getters['member/isMockCredit']
        )
      } catch (error) {
        console.error('[WitcherHeader] Error fetching credit:', error)
        // Keep previous balance on error
      }
    },
    async refreshBalance() {
      // Refresh promo status and balance
      await this.checkFreePromo()
      this.fetchGameRestriction({ force: true })
      await this.fetchCredit({ force: true })

      // Use displayBalance getter
      const balance = this.$store.getters['member/displayBalance']
      const diamondBalance = this.$store.getters['member/diamondBalance']
      this.userBalance = balance
      this.animateBalanceChange(this.userBalance)
      this.animateDiamondBalanceChange(diamondBalance)
      console.log('[WitcherHeader] Balance refreshed')
    },
    ...mapActions('member', [
      'fetchCredit',
      'checkFreePromo',
      'fetchGameRestriction'
    ]),
    animateBalanceChange(newBalance) {
      console.log(
        `[WitcherHeader] Animating balance from ${this.animatedBalance} to ${newBalance}`
      )

      const startBalance = this.animatedBalance
      const difference = newBalance - startBalance

      // If no difference, no need to animate
      if (difference === 0) {
        console.log('[WitcherHeader] No balance change, skipping animation')
        return
      }

      const startTime = performance.now()
      // Throttle to 30fps for better performance (saves CPU cycles)
      const frameInterval = 1000 / 30
      let lastFrameTime = startTime

      const animate = currentTime => {
        // Throttle animation to 30fps
        if (currentTime - lastFrameTime < frameInterval) {
          requestAnimationFrame(animate)
          return
        }
        lastFrameTime = currentTime

        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / this.animationDuration, 1)

        // Use easing function for smooth animation
        const easeOutQuart = 1 - (1 - progress) ** 4

        this.animatedBalance = Math.round(
          startBalance + difference * easeOutQuart
        )

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.animatedBalance = newBalance
          console.log(`[WitcherHeader] Animation completed: ${newBalance}`)
        }
      }

      requestAnimationFrame(animate)
    },
    animateDiamondBalanceChange(newBalance) {
      const startBalance = this.animatedDiamondBalance
      const difference = newBalance - startBalance

      if (difference === 0) {
        return
      }

      const startTime = performance.now()
      const frameInterval = 1000 / 30
      let lastFrameTime = startTime

      const animate = currentTime => {
        if (currentTime - lastFrameTime < frameInterval) {
          requestAnimationFrame(animate)
          return
        }
        lastFrameTime = currentTime

        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / this.animationDuration, 1)
        const easeOutQuart = 1 - (1 - progress) ** 4

        this.animatedDiamondBalance = Math.round(
          startBalance + difference * easeOutQuart
        )

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          this.animatedDiamondBalance = newBalance
        }
      }

      requestAnimationFrame(animate)
    }
  },
  computed: {
    ...mapState('member', ['credit']),
    isUserLoggedIn() {
      // Use reactive local state instead of direct localStorage check
      return this.userLoggedIn
    }
  },
  emits: [
    'action-button-click',
    'menu-click',
    'registration',
    'login',
    'show-login-modal',
    'login-success'
  ],
  mounted() {
    // Load initial balance if logged in
    if (this.userLoggedIn) {
      // Delay initial balance load to ensure component is fully rendered
      this.$nextTick(() => {
        setTimeout(() => {
          this.getCredit()
        }, 500) // Small delay for better visual effect
      })
    } else {
      this.loadInitialBalance()
    }

    // Listen for authentication events via event bus
    this.$eventBus.$on('user-logged-in', this.handleUserLogin)
    this.$eventBus.$on('user-logged-out', this.handleUserLogout)

    // Listen for balance updates via event bus
    this.$eventBus.$on('balanceUpdated', this.handleBalanceUpdate)

    // Listen for balance refresh requests (e.g., after deposit/withdraw)
    this.$eventBus.$on('refreshBalance', this.refreshBalance)

    // Listen for show register modal event (e.g., from promo_id URL param)
    this.$root.$on('show-register-modal', this.showRegisterModal)
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$eventBus.$off('user-logged-in', this.handleUserLogin)
    this.$eventBus.$off('user-logged-out', this.handleUserLogout)
    this.$eventBus.$off('balanceUpdated', this.handleBalanceUpdate)
    this.$eventBus.$off('refreshBalance', this.refreshBalance)
    this.$root.$off('show-register-modal', this.showRegisterModal)
  }
}
</script>

<style lang="scss" scoped>
.witcher-header {
  position: sticky;
  top: 0;
  background: var(--theme-header-bg) !important;
  // Promote to compositor layer for smoother scroll
  will-change: transform;
  // Reduce blur on mobile for better scroll performance
  @media (max-width: 768px) {
    backdrop-filter: blur(5px);
  }
  @media (min-width: 769px) {
    backdrop-filter: blur(10px);
  }
  border-bottom: 4px solid var(--theme-secondary-light);
  border-radius: 0 0 30px 30px; // Rounded bottom corners
  z-index: 100;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;

  .header-container {
    display: flex;
    flex-direction: column; // Stack vertically: main-header + quick-actions
    position: relative;
    padding-bottom: 5px;
    background: inherit; // Inherit background from parent
    border-radius: inherit; // Inherit border-radius from parent
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .main-header {
    display: flex; // 2 columns: logo | action-section
    height: clamp(60px, 10vh, 80px);
    min-height: 60px;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .logo-section {
    width: clamp(140px, 14vw, 140px); // Column 1: Logo - more responsive
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0; // Don't allow logo to shrink

    .logo-box {
      padding-left: 10px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      .logo-image {
        width: clamp(115px, 10vw, 115px);
        height: auto;
        max-width: 100%;
      }
    }
  }

  .action-section {
    flex: 1; // Column 2: Action section (takes remaining space)
    display: flex;
    flex-direction: column; // 2 rows: action-row + announcement-section
    min-width: 0;
    max-width: calc(100vw - clamp(110px, 12vw, 120px));
    overflow-x: hidden;

    .action-row {
      flex: 1;
      display: flex;
      align-items: center;
      padding: 0 clamp(10px, 2vw, 20px);
      position: relative;
      min-height: clamp(50px, 7vh, 65px);

      // Auth transition animations
      .auth-fade-enter-active,
      .auth-fade-leave-active {
        transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
      }

      .auth-fade-enter {
        opacity: 0;
        transform: translateX(20px) scale(0.9);
      }

      .auth-fade-leave-to {
        opacity: 0;
        transform: translateX(-20px) scale(0.9);
      }

      // Logged out state - auth buttons
      .auth-buttons {
        display: flex;
        align-items: center;
        gap: clamp(8px, 1.5vw, 15px);
        width: 100%;

        .action-btn {
          border-radius: clamp(8px, 1.2vw, 12px) !important;
          font-size: clamp(14px, 1.8vw, 18px) !important;
          padding: clamp(8px, 1.2vh, 12px) clamp(16px, 2.8vw, 24px) !important;
          white-space: nowrap;
          flex: 1;
          min-height: clamp(32px, 4.5vh, 40px);
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1.2;
          color: #f0e7e7;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          }

          @media (max-width: 480px) {
            font-size: clamp(13px, 2.5vw, 16px) !important;
            padding: clamp(7px, 1.5vh, 10px) clamp(12px, 3vw, 18px) !important;
            min-height: clamp(30px, 4vh, 38px);
          }

          @media (max-width: 360px) {
            font-size: clamp(12px, 3vw, 14px) !important;
            padding: clamp(6px, 1.2vh, 8px) clamp(10px, 3.5vw, 16px) !important;
            min-height: clamp(28px, 3.5vh, 35px);
          }
        }

        .theme-outline-btn {
          background: transparent !important;
          border: 2px solid var(--theme-primary) !important;
          color: #f0e7e7 !important;

          &:hover {
            background: rgba(var(--theme-primary-rgb), 0.15) !important;
          }
        }

        .theme-filled-btn {
          background: var(--theme-gradient-primary) !important;
          border: none !important;
          color: #f0e7e7 !important;

          &:hover {
            box-shadow: 0 4px 12px rgba(var(--theme-primary-rgb), 0.4);
          }
        }
      }

      // Logged in state - Minimal user controls
      .user-controls {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        width: 100%;
        gap: 8px;

        // Balance box - fixed width, minimal (uses gold/secondary)
        .balance-box {
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-width: 100px;
          padding: 6px 10px;
          background: rgba(var(--theme-secondary-rgb), 0.08);
          border: 1px solid rgba(var(--theme-secondary-rgb), 0.2);
          border-radius: 6px;

          .balance-icon {
            color: var(--theme-secondary);
            flex-shrink: 0;
          }

          .balance-value {
            font-size: 13px;
            font-weight: 600;
            color: #fff;
            font-variant-numeric: tabular-nums;
          }
        }

        // Diamond box - similar to balance box with blue/purple theme
        .diamond-box {
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-width: 80px;
          padding: 6px 10px;
          background: rgba(100, 149, 237, 0.08);
          border: 1px solid rgba(100, 149, 237, 0.2);
          border-radius: 6px;

          .diamond-icon {
            font-size: 14px;
            flex-shrink: 0;
          }

          .diamond-value {
            font-size: 13px;
            font-weight: 600;
            color: #fff;
            font-variant-numeric: tabular-nums;
          }
        }

        // Menu button - pure CSS hamburger with animation
        .menu-btn {
          width: 36px;
          height: 36px;
          padding: 8px;
          background: transparent;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: center;
          gap: 4px;
          transition: background 0.15s ease;

          .menu-line {
            width: 18px;
            height: 2px;
            background: #fff;
            border-radius: 1px;
            transition: all 0.2s ease;
            transform-origin: right center;

            &.short {
              width: 12px;
            }
          }

          &:hover .menu-line {
            background: var(--theme-secondary);

            &.short {
              width: 18px;
            }
          }

          &:active {
            background: rgba(255, 255, 255, 0.08);

            .menu-line {
              background: var(--theme-secondary);
            }
          }
        }
      }
    }

    .announcement-section {
      // Row 2: Announcement section
      height: clamp(23px, 2.8vh, 27px);
      display: flex;
      align-items: center;

      .announcement-icon {
        width: clamp(26px, 5vw, 60px);
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        flex-shrink: 0;
        padding-right: 10px;
      }

      .announcement-bar {
        flex: 1;
        height: 100%;
        overflow: hidden;

        .marquee-container {
          height: 100%;
          display: flex;
          align-items: center;

          .marquee-text {
            color: #fff;
            font-size: clamp(15px, 1.6vw, 16px);
            white-space: nowrap;
            animation: slideUpThenMarquee 20s linear infinite;
          }
        }
      }
    }
  }

  .quick-actions-row {
    display: flex;
    width: clamp(70%, 75%, 75%);
    align-self: center;
    justify-content: center;
    gap: clamp(4px, 1vw, 8px);
    padding-bottom: 5px;

    .action-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: clamp(6px, 1.2vh, 10px) clamp(4px, 0.8vw, 8px);
      background: #544663;
      border-radius: clamp(6px, 1vw, 10px);
      cursor: pointer;
      transition: all 0.3s ease;
      min-height: clamp(40px, 8vh, 60px);
      min-width: clamp(70px, 8vh, 80px);

      .action-icon {
        font-size: clamp(16px, 3.5vw, 24px);
        margin-bottom: clamp(2px, 0.5vh, 4px);
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 480px) {
          font-size: 18px;
          margin-bottom: 2px;
        }
      }

      .action-label {
        color: #fff;
        font-size: clamp(11px, 1.8vw, 11px);
        text-align: center;
        white-space: nowrap;
        line-height: 1.2;

        @media (max-width: 480px) {
          font-size: 12px;
        }

        @media (max-width: 360px) {
          font-size: 11px;
        }
      }
    }
  }
}

@keyframes slideUpThenMarquee {
  0% {
    transform: translateY(100%) translateX(0%);
  }
  10% {
    transform: translateY(0%) translateX(0%);
  }
  100% {
    transform: translateY(0%) translateX(-100%);
  }
}

@keyframes marquee {
  0% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
}

// Dark Theme Override for sidebar
::v-deep .b-sidebar {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
}

::v-deep .b-sidebar.b-sidebar-right {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
}

::v-deep .b-sidebar-body {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
  border-left: 1px solid rgba(120, 119, 198, 0.3) !important;

  // Prevent content dragging and scrolling issues
  touch-action: pan-y !important;
  overscroll-behavior-x: none !important;
  overflow-x: hidden !important;
  position: fixed !important;
}

::v-deep .b-sidebar-header {
  background: rgba(10, 10, 10, 0.95) !important;
  color: #fff !important;
  border-bottom: 1px solid rgba(120, 119, 198, 0.2) !important;
}

::v-deep .witcher-sidebar-body {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
}

::v-deep .witcher-sidebar-header {
  background: rgba(10, 10, 10, 0.95) !important;
  color: #fff !important;
  border-bottom: 1px solid rgba(120, 119, 198, 0.2) !important;
}

::v-deep #witcher-sidebar {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;
  z-index: 99999 !important;

  * {
    background: transparent !important;
    color: inherit !important;
  }

  .b-sidebar-body,
  .b-sidebar-header,
  .sidebar-body,
  .sidebar-header {
    background: var(--theme-sidebar-bg) !important;
    color: #fff !important;
    z-index: 99999 !important;
  }
}

.witcher-sidebar-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;

  .sidebar-logo {
    display: flex;
    align-items: center;
    gap: 12px;

    .sidebar-logo-img {
      width: 32px;
      height: 20px;
      object-fit: contain;
    }

    .sidebar-title {
      color: #fff;
      margin: 0;
      font-weight: 600;
      background: linear-gradient(
        45deg,
        var(--theme-secondary),
        var(--theme-secondary-light)
      );
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  .sidebar-close-btn {
    background: rgba(var(--theme-secondary-rgb), 0.1);
    border: 1px solid rgba(var(--theme-secondary-rgb), 0.3);
    border-radius: 6px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
  }
}

.witcher-sidebar-content {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  background: transparent !important;

  // Prevent dragging and elastic scrolling
  touch-action: pan-y !important;
  overscroll-behavior: none !important;
  -webkit-overflow-scrolling: touch !important;
  overflow-x: hidden !important;
  user-select: none !important;
}

.sidebar-profile {
  padding: 20px;
  border-bottom: 1px solid rgba(var(--theme-secondary-rgb), 0.1);
  display: flex;
  align-items: center;
  gap: 15px;

  .profile-avatar {
    width: 50px;
    height: 50px;
    background: linear-gradient(
      45deg,
      rgba(var(--theme-secondary-rgb), 0.2),
      rgba(var(--theme-secondary-rgb), 0.1)
    );
    border: 2px solid rgba(var(--theme-secondary-rgb), 0.3);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }

  .profile-info {
    .profile-name {
      color: #fff;
      margin: 0;
      font-weight: 600;
      font-size: 16px;
    }

    .profile-status {
      color: rgba(255, 255, 255, 0.6);
      font-size: 12px;
    }
  }
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;

  .nav-section-title {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin: 20px 20px 10px;

    &:first-child {
      margin-top: 0;
    }
  }

  .nav-item {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    margin: 2px 15px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    gap: 12px;
    position: relative;

    .nav-icon {
      color: rgba(255, 255, 255, 0.7);
      transition: color 0.3s ease;
    }

    .nav-text {
      color: #fff;
      font-weight: 500;
      flex: 1;
    }

    .nav-arrow {
      color: rgba(255, 255, 255, 0.3);
      opacity: 0;
      transition: all 0.3s ease;
    }
  }
}

.sidebar-footer {
  padding: 20px;
  border-top: 1px solid rgba(var(--theme-secondary-rgb), 0.1);
  text-align: center;

  .footer-text {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    margin: 0;
  }

  .footer-version {
    color: rgba(255, 255, 255, 0.3);
    font-size: 10px;
    margin: 5px 0 0;
  }
}

// Global overrides for Bootstrap Vue Sidebar to ensure dark theme
::v-deep .b-sidebar {
  background: var(--theme-sidebar-bg) !important;
  color: #fff !important;

  .b-sidebar-body,
  .sidebar-body {
    background: var(--theme-sidebar-bg) !important;
    color: #fff !important;
  }

  .b-sidebar-header,
  .sidebar-header {
    background: rgba(10, 10, 10, 0.95) !important;
    color: #fff !important;
    border-bottom: 1px solid rgba(120, 119, 198, 0.2) !important;
  }

  // Override any white backgrounds
  * {
    background-color: transparent !important;
  }

  // Ensure text is white
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  span,
  div {
    color: inherit !important;
  }
}
</style>
